import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { PhotoLightbox } from "@/components/PhotoLightbox";
import { ArrowLeft, Images, Plus, Share2, Globe2, Lock, Check, Trash2, ExternalLink, Camera, Star } from "lucide-react";

export default function AlbumDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [album, setAlbum] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openAdd, setOpenAdd] = useState(false);
  const [availableKegiatan, setAvailableKegiatan] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [lightbox, setLightbox] = useState({ open: false, photos: [], index: 0, title: "" });

  const canManage = ["SUPER_ADMIN","ADMIN_KLASIS","SEKRETARIS_KLASIS","GEMBALA_JEMAAT","SEKRETARIS_JEMAAT"].includes(user?.role);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/albums/${id}`);
      setAlbum(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Album tidak ditemukan"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]);

  const openAddDialog = async () => {
    try {
      // Fetch kegiatan candidates (scope by role handled at backend)
      const q = album?.church_id ? `?church_id=${album.church_id}` : "";
      const { data } = await api.get(`/kegiatan${q}`);
      // Exclude already in album
      const existing = new Set(album?.kegiatan_ids || []);
      const withPhotos = data.filter(k => (k.photo_count || 0) > 0 && !existing.has(k.id));
      setAvailableKegiatan(withPhotos);
      setSelected(new Set());
      setOpenAdd(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggle = (kid) => {
    const s = new Set(selected);
    s.has(kid) ? s.delete(kid) : s.add(kid);
    setSelected(s);
  };

  const addSelected = async () => {
    if (selected.size === 0) { toast.error("Pilih minimal 1 kegiatan"); return; }
    setSaving(true);
    try {
      const newIds = [...(album.kegiatan_ids || []), ...Array.from(selected)];
      await api.patch(`/albums/${id}`, { kegiatan_ids: newIds });
      toast.success(`${selected.size} kegiatan ditambahkan`);
      setOpenAdd(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const removeKegiatan = async (kid) => {
    if (!window.confirm("Hapus kegiatan ini dari album? Foto asli tetap ada.")) return;
    try {
      const newIds = (album.kegiatan_ids || []).filter(x => x !== kid);
      const patch = { kegiatan_ids: newIds };
      if (album.cover_kegiatan_id === kid) patch.cover_kegiatan_id = null;
      await api.patch(`/albums/${id}`, patch);
      toast.success("Dihapus dari album");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const setCover = async (kid) => {
    try {
      await api.patch(`/albums/${id}`, { cover_kegiatan_id: kid });
      toast.success("Cover album diperbarui");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const togglePublic = async () => {
    try {
      await api.patch(`/albums/${id}`, { is_public: !album.is_public });
      toast.success(album.is_public ? "Album disembunyikan dari publik" : "Album dijadikan publik");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const share = () => {
    const url = `${window.location.origin}/public/album/${album.slug}`;
    navigator.clipboard?.writeText(url);
    toast.success("Link publik disalin");
  };

  const openLightbox = (kegiatan, startIdx = 0) => {
    setLightbox({ open: true, photos: kegiatan.photos_base64 || [], index: startIdx, title: kegiatan.title });
  };

  const totalPhotos = useMemo(() => (album?.kegiatan || []).reduce((sum, k) => sum + (k.photo_count || (k.photos_base64?.length || 0)), 0), [album]);

  if (loading) return <AppShell><div className="p-12 text-center text-sm text-muted-foreground">Memuat album…</div></AppShell>;
  if (!album) return <AppShell><div className="p-12 text-center text-sm text-muted-foreground">Album tidak ditemukan.</div></AppShell>;

  return (
    <AppShell>
      <div className="mb-4">
        <Link to="/album" className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1" data-testid="link-back-albums">
          <ArrowLeft className="h-3 w-3" /> Kembali ke daftar album
        </Link>
      </div>

      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div className="flex-1 min-w-0">
          <div className="text-xs tracking-[0.18em] uppercase text-muted-foreground">Album Kegiatan</div>
          <h1 className="font-heading font-bold text-3xl mt-1 flex items-center gap-3">
            <Images className="h-7 w-7 text-secondary" /> {album.name}
          </h1>
          {album.description && <p className="text-sm text-muted-foreground mt-2 max-w-2xl">{album.description}</p>}
          <div className="text-xs text-muted-foreground mt-2 flex items-center gap-4 flex-wrap">
            <span>{album.church_short_name ? `Jemaat ${album.church_short_name}` : "Klasis"}</span>
            <span>•</span>
            <span>{album.kegiatan?.length || 0} kegiatan · {totalPhotos} foto</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canManage && (
            <>
              <div className="flex items-center gap-2 border border-border rounded-full px-3 py-1.5">
                {album.is_public ? <Globe2 className="h-3.5 w-3.5 text-emerald-600" /> : <Lock className="h-3.5 w-3.5 text-muted-foreground" />}
                <span className="text-xs">{album.is_public ? "Publik" : "Internal"}</span>
                <Switch checked={!!album.is_public} onCheckedChange={togglePublic} data-testid="switch-detail-public" />
              </div>
            </>
          )}
          {album.is_public && (
            <Button variant="outline" size="sm" className="rounded-full" onClick={share} data-testid="btn-share-detail">
              <Share2 className="h-3.5 w-3.5 mr-1.5" /> Bagikan
            </Button>
          )}
          {canManage && (
            <Button size="sm" className="rounded-full" onClick={openAddDialog} data-testid="btn-add-kegiatan-album">
              <Plus className="h-3.5 w-3.5 mr-1.5" /> Tambah Kegiatan
            </Button>
          )}
        </div>
      </div>

      {(!album.kegiatan || album.kegiatan.length === 0) ? (
        <div className="border border-dashed border-border rounded-lg p-12 text-center" data-testid="album-empty">
          <Camera className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
          <div className="text-sm text-muted-foreground">Album masih kosong. {canManage && <>Klik <strong>Tambah Kegiatan</strong> untuk mulai.</>}</div>
        </div>
      ) : (
        <div className="space-y-6" data-testid="album-content">
          {album.kegiatan.map((k) => (
            <Card key={k.id} className="overflow-hidden">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="font-heading font-bold text-sm">{k.title}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{new Date(k.date).toLocaleDateString("id-ID", { day: "2-digit", month: "long", year: "numeric" })} · {k.category}</div>
                  </div>
                  {canManage && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setCover(k.id)}
                        className={`text-xs flex items-center gap-1 px-2 py-1 rounded-full border ${album.cover_kegiatan_id === k.id ? "bg-secondary text-secondary-foreground border-secondary" : "border-border text-muted-foreground hover:border-primary"}`}
                        title="Jadikan cover album"
                        data-testid={`btn-set-cover-${k.id}`}
                      >
                        <Star className={`h-3 w-3 ${album.cover_kegiatan_id === k.id ? "fill-current" : ""}`} /> {album.cover_kegiatan_id === k.id ? "Cover" : "Set Cover"}
                      </button>
                      <button onClick={() => removeKegiatan(k.id)} className="text-xs text-muted-foreground hover:text-destructive" data-testid={`btn-remove-keg-${k.id}`}>
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                  {(k.photos_base64 || []).map((src, i) => (
                    <button key={i} onClick={() => openLightbox(k, i)} className="aspect-square rounded overflow-hidden bg-muted group" data-testid={`thumb-${k.id}-${i}`}>
                      <img src={src} alt={`${k.title} ${i+1}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add kegiatan dialog */}
      <Dialog open={openAdd} onOpenChange={setOpenAdd}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Tambah Kegiatan ke Album</DialogTitle>
            <DialogDescription>Pilih kegiatan yang memiliki foto. Kegiatan tanpa foto tidak ditampilkan.</DialogDescription>
          </DialogHeader>
          {availableKegiatan.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center">Tidak ada kegiatan berfoto yang bisa ditambahkan.</div>
          ) : (
            <div className="max-h-96 overflow-y-auto space-y-2 py-2">
              {availableKegiatan.map((k) => {
                const on = selected.has(k.id);
                return (
                  <button
                    key={k.id}
                    onClick={() => toggle(k.id)}
                    className={`w-full text-left p-3 border rounded-md flex items-center gap-3 transition-colors ${on ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}
                    data-testid={`opt-kegiatan-${k.id}`}
                  >
                    <div className={`h-5 w-5 rounded border-2 flex items-center justify-center shrink-0 ${on ? "bg-primary border-primary" : "border-muted-foreground"}`}>
                      {on && <Check className="h-3 w-3 text-primary-foreground" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{k.title}</div>
                      <div className="text-[0.7rem] text-muted-foreground">{new Date(k.date).toLocaleDateString("id-ID")} · {k.photo_count} foto · {k.category}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpenAdd(false)}>Batal</Button>
            <Button onClick={addSelected} disabled={saving || selected.size === 0} data-testid="btn-confirm-add-kegiatan">
              {saving ? "Menambahkan…" : `Tambahkan ${selected.size} kegiatan`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PhotoLightbox
        open={lightbox.open}
        photos={lightbox.photos}
        initialIndex={lightbox.index}
        onClose={() => setLightbox({ ...lightbox, open: false })}
        title={lightbox.title}
      />
    </AppShell>
  );
}
